import { useState, useEffect, useCallback } from 'react';
import { Shield, Smartphone, Copy, Check, AlertTriangle, RefreshCw, X } from 'lucide-react';

async function api(path, options = {}) {
  const res = await fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json', ...options.headers }, ...options });
  return res.json();
}

export default function MfaSetup() {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState('loading'); // loading, status, setup, verify, backup, done
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [backupsCopied, setBackupsCopied] = useState(false);

  const loadStatus = useCallback(async () => {
    const data = await api('/api/auth/mfa/status');
    if (data.success) {
      setStatus(data);
      setStep('status');
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleStartSetup = async () => {
    setError('');
    setLoading(true);
    const data = await api('/api/auth/mfa/setup', { method: 'POST' });
    setLoading(false);
    if (data.error) return setError(data.error);
    setQrCode(data.qrCode);
    setSecret(data.secret);
    setStep('setup');
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (verifyCode.length !== 6) return setError('Enter the 6-digit code from your authenticator app');
    setLoading(true);
    const data = await api('/api/auth/mfa/verify', { method: 'POST', body: JSON.stringify({ token: verifyCode }) });
    setLoading(false);
    if (data.error) return setError(data.error);
    setBackupCodes(data.backupCodes);
    setStep('backup');
  };

  const handleDisable = async () => {
    if (!confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) return;
    setLoading(true);
    const data = await api('/api/auth/mfa/disable', { method: 'POST' });
    setLoading(false);
    if (data.error) return setError(data.error);
    await loadStatus();
  };

  const handleRegenerateBackupCodes = async () => {
    if (!confirm('This will invalidate all existing backup codes. Continue?')) return;
    setLoading(true);
    const data = await api('/api/auth/mfa/backup-codes', { method: 'POST' });
    setLoading(false);
    if (data.error) return setError(data.error);
    setBackupCodes(data.backupCodes);
    setStep('backup');
  };

  const copyToClipboard = (text, setter) => {
    navigator.clipboard.writeText(text).then(() => { setter(true); setTimeout(() => setter(false), 2000); });
  };

  const cardStyle = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 20 };
  const inputStyle = { background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '10px 14px', color: '#e2e8f0', fontSize: 18, textAlign: 'center', letterSpacing: 8, width: 200 };

  if (step === 'loading') {
    return <div style={{ padding: 20, color: 'var(--text-tertiary)' }}>Loading MFA status...</div>;
  }

  // ── Status view (MFA enabled or not) ──
  if (step === 'status') {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Smartphone size={18} style={{ color: '#00d4aa' }} />
              Two-Factor Authentication
            </h3>
            <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>
              {status?.required ? 'Required for your role' : 'Recommended for added security'}
            </p>
          </div>
          <div className="mfa-status-badge-wrap">
            {status?.enabled ? (
              <span className="mfa-status-badge" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>Enabled</span>
            ) : (
              <span className="mfa-status-badge" style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>Not Enabled</span>
            )}
          </div>
        </div>

        {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '8px 12px', marginTop: 12, color: '#ef4444', fontSize: 13 }}>{error}</div>}

        {status?.enabled ? (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
              <Shield size={16} style={{ color: '#22c55e' }} />
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Your account is protected with TOTP authentication</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 16 }}>
              Backup codes remaining: <strong style={{ color: status.remainingBackupCodes > 2 ? '#22c55e' : '#f59e0b' }}>{status.remainingBackupCodes}</strong>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleRegenerateBackupCodes} disabled={loading} className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <RefreshCw size={14} /> New Backup Codes
              </button>
              <button onClick={handleDisable} disabled={loading} className="btn btn-sm" style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <X size={14} /> Disable MFA
              </button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 16 }}>
            {status?.required && (
              <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={14} style={{ color: '#f59e0b' }} />
                <span style={{ fontSize: 13, color: '#f59e0b' }}>Two-factor authentication is required for admin accounts.</span>
              </div>
            )}
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Protect your account with a time-based one-time password (TOTP). You will need an authenticator app like Google Authenticator, Microsoft Authenticator, or Authy.
            </p>
            <button onClick={handleStartSetup} disabled={loading} className="btn btn-primary btn-sm" style={{ background: '#00d4aa', color: '#0a0a0f', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Smartphone size={14} /> Set Up Two-Factor Authentication
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Setup: scan QR code ──
  if (step === 'setup') {
    return (
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Step 1: Scan QR Code</h3>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
          Open your authenticator app and scan this QR code. If you cannot scan it, enter the secret key manually.
        </p>

        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 12, display: 'inline-block' }}>
            <img src={qrCode} alt="MFA QR Code" style={{ width: 180, height: 180, display: 'block' }} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4 }}>Manual entry key:</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <code style={{ background: 'rgba(0,0,0,0.3)', padding: '6px 10px', borderRadius: 6, fontSize: 13, letterSpacing: 2, color: '#00d4aa', fontFamily: 'monospace' }}>
                {secret}
              </code>
              <button onClick={() => copyToClipboard(secret, setCopied)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 4 }}>
                {copied ? <Check size={14} style={{ color: '#22c55e' }} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 24 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600 }}>Step 2: Enter Verification Code</h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
            Enter the 6-digit code shown in your authenticator app to confirm setup.
          </p>
          {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, color: '#ef4444', fontSize: 13 }}>{error}</div>}
          <form onSubmit={handleVerify} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <input
              type="text" inputMode="numeric" maxLength={6} autoComplete="one-time-code" autoFocus
              value={verifyCode} onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000" style={inputStyle}
            />
            <button type="submit" disabled={loading || verifyCode.length !== 6} className="btn btn-primary btn-sm" style={{ background: '#00d4aa', color: '#0a0a0f' }}>
              {loading ? 'Verifying...' : 'Verify & Enable'}
            </button>
            <button type="button" onClick={() => { setStep('status'); setError(''); }} className="btn btn-secondary btn-sm">
              Cancel
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Backup codes ──
  if (step === 'backup') {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Shield size={20} style={{ color: '#22c55e' }} />
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#22c55e' }}>Two-Factor Authentication Enabled</h3>
        </div>

        <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <p style={{ fontSize: 13, color: '#f59e0b', margin: 0, fontWeight: 600 }}>
            Save your backup codes now. They will not be shown again.
          </p>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            Use a backup code to sign in if you lose access to your authenticator app. Each code can only be used once.
          </p>
        </div>

        <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {backupCodes.map((code, i) => (
              <code key={i} style={{ fontFamily: 'monospace', fontSize: 14, color: 'var(--text-primary)', padding: '4px 8px', background: 'rgba(255,255,255,0.05)', borderRadius: 4, textAlign: 'center' }}>
                {code}
              </code>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => copyToClipboard(backupCodes.join('\n'), setBackupsCopied)}
            className="btn btn-secondary btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {backupsCopied ? <Check size={14} style={{ color: '#22c55e' }} /> : <Copy size={14} />}
            {backupsCopied ? 'Copied!' : 'Copy All Codes'}
          </button>
          <button
            onClick={() => { setStep('status'); loadStatus(); }}
            className="btn btn-primary btn-sm"
            style={{ background: '#00d4aa', color: '#0a0a0f' }}
          >
            I've Saved My Codes
          </button>
        </div>
      </div>
    );
  }

  return null;
}
